<template>
    <div id="org_settings_prioritization">
        <div class="d-flex">
            <img src="/img/icons/nav-icons/Insights.svg" alt="Insights Prioritization" class="card-icon"></img>
        
            <div class="ms-3 flex-grow-1">
                <div class="d-flex align-items-center justify-content-between">
                    <h3 class="mb-0">Insight Prioritization</h3>
                    <!-- Auto-save status indicator -->
                    <div v-show="user_prioritization.length !== 0">
                        <small v-if="form.busy" class="text-muted">
                            <i class="fa fa-spinner fa-spin me-1"></i> Saving...
                        </small>
                        <small v-else-if="form.lastSaved" class="text-success">
                            <i class="fa fa-check me-1"></i> Saved
                        </small>
                    </div>
                </div>
                <p>Set which insights matter most to you.</p>
            </div>
        </div>
    
        <div v-show="user_prioritization.length === 0">
            <h4 class="d-flex align-items-center">
                <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
                Loading Prioritization Options
            </h4>
        </div>
        
        <div v-for="(group, groupLabel, index) in grouped_prioritization" :key="groupLabel" v-show="user_prioritization.length > 0">
            <h4 class="mb-3">{{ groupLabel }}</h4>
            <div class="row" v-for="p in group" :key="p.filter">
                <div class="col mb-4">
                    <div class="slider-container">
                        <div class="slider-label">{{ p.text }}</div>
                        <div class="slider-wrapper">
                            <div class="slider-row">
                                <span class="range-label range-label-low">Low</span>
                                <VueSlider
                                    class="single-color-slider"
                                    v-model="p.value"
                                    :tooltip-formatter="val => Math.round(p.value*100) +'%'"
                                    :min="0.1"
                                    :max="1"
                                    :interval="0.1"
                                    :dot-attrs="{ 'aria-label': p.text + ' prioritization' }"
                                    @change="onSliderChange"
                                />
                                <span class="range-label range-label-high">High</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <hr v-if="index < Object.keys(grouped_prioritization).length - 1" class="mb-4" style="color: #DEEAFF; height: 3px; border: none; background-color: #DEEAFF;">
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import VueSlider from 'vue-3-slider-component';
import permissionsMixin from '../../../mixins/permissions';
import { useNotifications } from '../../../composables/useNotifications';

export default defineComponent({
    mixins: [permissionsMixin],
    name: 'PrioritizationSettings',
    setup() {
        const { showNotification } = useNotifications();
        return {
            showNotification
        };
    },
    components: {
        VueSlider,
    },
    props: {
        client: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            local_client: {},
            form: {
                busy: false,
                error: false,
                lastSaved: false
            },
            user_prioritization: [],
            grouped_prioritization: {},
            saveTimeout: null
        };
    },
    beforeMount() {
        this.local_client = this.client;
        this.getPrioritizationOptions();
    },
    beforeUnmount() {
        // Clear any pending save timeout
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
        }
    },
    methods: {
        getPrioritizationOptions() {
            window.axios.post('/api/bigquery/' + this.client.url + '/prioritization_columns', [])
                .then(response => {
                    this.user_prioritization = response.data.options;
                    this.groupPrioritizationData();
                    
                    // Emit event to parent to indicate if prioritization data is available
                    this.$emit('prioritization-loaded', !!(this.user_prioritization && this.user_prioritization.length > 0));
                })
                .catch(error => {
                    console.error('Error loading prioritization options:', error);
                    // Emit false if there's an error loading data
                    this.$emit('prioritization-loaded', false);
                });
        },
        groupPrioritizationData() {
            this.grouped_prioritization = {};
            this.user_prioritization.forEach(item => {
                if (!this.grouped_prioritization[item.label]) {
                    this.grouped_prioritization[item.label] = [];
                }
                this.grouped_prioritization[item.label].push(item);
            });
        },
        onSliderChange() {
            // Clear existing timeout
            if (this.saveTimeout) {
                clearTimeout(this.saveTimeout);
            }
            
            // Set new timeout for debounced save
            this.saveTimeout = setTimeout(() => {
                this.autoSave();
            }, 1000); // 1 second debounce
        },
        autoSave() {
            const self = this;
            this.form.busy = true;
            this.form.lastSaved = false;

            if (this.user_prioritization.length > 0) {
                this.local_client.prioritization_preference = this.user_prioritization;

                window.axios.put('/api/client/' + this.local_client.url, this.local_client)
                    .then(response => {
                        self.form.busy = false;
                        self.form.lastSaved = true;
                        
                        // Hide the "Saved" indicator after 3 seconds
                        setTimeout(() => {
                            self.form.lastSaved = false;
                        }, 3000);
                        
                        self.showNotification({
                            text: 'Your prioritization preferences were successfully saved!'
                        });
                    }).catch(error => {
                        self.form.busy = false;
                        self.form.error = true;
                        setTimeout(() => { self.form.error = false }, 10000);
                        self.showNotification({ 
                            text: 'Your prioritization preferences failed to save. Please try again.',
                            type: 'error'
                        });
                    });
            }
        },
        update() {
            const self = this;
            this.form.busy = true;

            if (this.user_prioritization.length > 0) {
                this.local_client.prioritization_preference = this.user_prioritization;

                window.axios.put('/api/client/' + this.local_client.url, this.local_client)
                    .then(response => {
                        self.form.busy = false;
                        self.showNotification({
                            text: 'Your prioritization preferences were successfully saved!'
                        });
                    }).catch(error => {
                        self.form.busy = false;
                        self.form.error = true;
                        setTimeout(() => { self.form.error = false }, 10000);
                        self.showNotification({
                            text: 'Failed to update your prioritization preferences. Please try again.',
                            type: 'error'
                        });
                    });
            }
        },
    }
});
</script>
